/**
 * Account creation for the invitee flows: accept the invitation, then sign
 * in with the password just set and store the session exactly as the shell
 * does after a normal login (`useShellStore.setUser`).
 *
 * The session is needed straight away: TOTP enrolment
 * (`/mfa/totp/enroll/`) and clearing the tour flag are authenticated calls.
 */
import type { User } from "../../../store";
import {
  acceptInvitation,
  isTokenLogin,
  login,
  obErrorCode,
  obFieldErrors,
  type InvitationErrorBody,
  ObApiError,
} from "../kit";
import { currentObLocale } from "../kit";

/** Error codes that mean the link itself is no longer usable. */
export const LINK_CODES = new Set(["missing_token", "invalid", "revoked", "accepted", "expired"]);

export type CreateAccountOutcome =
  /** Account created and signed in — the session is stored. */
  | { status: "signed-in" }
  /** Account created, but the automatic sign-in did not return a session. */
  | { status: "created" }
  /** The server rejected the password (`{password: [...]}`). */
  | { status: "password"; errors: string[] }
  /** The link stopped working (expired / revoked / accepted / invalid). */
  | { status: "link"; code: string; body: InvitationErrorBody | null }
  /** Anything else (email_taken, quota_exceeded, network …). */
  | { status: "error"; code?: string; detail?: string };

export interface CreateAccountArgs {
  token: string;
  password: string;
  /** Sent only when non-empty; the server falls back to the invitation's name. */
  name?: string;
  loginEmail: string;
  setUser: (user: User, accessToken: string, refreshToken?: string) => void;
}

/** People `User.language` for the active UI locale. */
export function peopleLanguage(): string {
  return currentObLocale() === "ar" ? "ar" : "en-us";
}

function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export async function createAccountAndSignIn({
  token,
  password,
  name,
  loginEmail,
  setUser,
}: CreateAccountArgs): Promise<CreateAccountOutcome> {
  const trimmed = name?.trim();
  try {
    await acceptInvitation({
      token,
      password,
      ...(trimmed ? { name: trimmed } : {}),
      language: peopleLanguage(),
      timezone: browserTimezone(),
    });
  } catch (err) {
    const code = obErrorCode(err);
    const fields = obFieldErrors(err);
    if (code === "invalid_password" || fields.password?.length) {
      return { status: "password", errors: fields.password ?? [] };
    }
    if (code && LINK_CODES.has(code)) {
      return { status: "link", code, body: err instanceof ObApiError ? err.body : null };
    }
    const detail = err instanceof ObApiError && err.status > 0 ? err.body?.detail : undefined;
    return { status: "error", code, detail };
  }

  // The language picked on these screens becomes the shell's UI locale too
  // (the shell reads "weco-locale" at boot).
  try {
    localStorage.setItem("weco-locale", currentObLocale());
  } catch {
    /* storage blocked — the in-memory locale still applies */
  }

  try {
    const res = await login(loginEmail, password);
    if (!isTokenLogin(res)) return { status: "created" };
    setUser(res.user, res.access, res.refresh);
    return { status: "signed-in" };
  } catch {
    return { status: "created" };
  }
}
